import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ArrowLeft, Search, RefreshCw, Calendar, CheckCircle, XCircle, 
  Clock, AlertTriangle, Zap, Eye, Filter, Users, Target, Phone,
  MessageCircle, Mail, GitBranch, DollarSign, FileText, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Trigger icons
const triggerIcons = {
  contact_created: Users,
  lead_created: Target,
  lead_updated: Target,
  deal_created: DollarSign,
  deal_stage_changed: GitBranch,
  deal_won: CheckCircle,
  call_completed: Phone,
  whatsapp_received: MessageCircle,
  form_submitted: FileText,
  booking_created: Calendar,
  task_completed: CheckCircle,
  scheduled: Clock
};

// Action icons
const actionIcons = {
  initiate_ai_call: Phone,
  send_whatsapp: MessageCircle,
  send_email: Mail,
  send_sms: MessageCircle,
  add_tag: Target,
  remove_tag: Target,
  assign_user: Users,
  assign_team: Users,
  update_status: GitBranch,
  create_task: FileText,
  send_notification: AlertTriangle,
  update_field: FileText,
  http_request: Zap,
  wait_delay: Clock,
  condition_branch: GitBranch
};

// Execution Detail Dialog
const ExecutionDetailDialog = ({ execution, open, onOpenChange }) => {
  if (!execution) return null;
  
  const result = execution.result || {};
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            {result.success && result.conditions_met ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : result.conditions_met === false ? (
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500" />
            )}
            Execution Details
          </DialogTitle>
          <DialogDescription>
            {new Date(execution.executed_at).toLocaleString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Summary Section */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <div className="text-sm text-slate-500">Workflow</div>
                  <div className="text-slate-900 font-medium">{execution.workflow_name}</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <div className="text-sm text-slate-500">Trigger</div>
                  <div className="text-slate-900 font-medium capitalize">
                    {execution.trigger?.replace(/_/g, ' ')}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <div className="text-sm text-slate-500">Status</div>
                  <div className="flex items-center gap-2">
                    {result.success && result.conditions_met ? (
                      <Badge className="bg-green-100 text-green-700">Success</Badge>
                    ) : result.conditions_met === false ? (
                      <Badge className="bg-amber-100 text-amber-700">Conditions Not Met</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">Failed</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <div className="text-sm text-slate-500">Duration</div>
                  <div className="text-slate-900 font-medium">
                    {result.duration_ms ? `${result.duration_ms}ms` : 'N/A'}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Context Summary */}
            {execution.context_summary && (
              <Card className="bg-slate-50 border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-900">Context Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {execution.context_summary.contact_name && (
                      <div>
                        <span className="text-slate-500">Contact: </span>
                        <span className="text-slate-900">{execution.context_summary.contact_name}</span>
                      </div>
                    )}
                    {execution.context_summary.phone && (
                      <div>
                        <span className="text-slate-500">Phone: </span>
                        <span className="text-green-600">Available</span>
                      </div>
                    )}
                    {execution.context_summary.email && (
                      <div>
                        <span className="text-slate-500">Email: </span>
                        <span className="text-green-600">Available</span>
                      </div>
                    )}
                    {execution.context_summary.contact_id && (
                      <div>
                        <span className="text-slate-500">Contact ID: </span>
                        <span className="text-slate-900 text-xs font-mono">{execution.context_summary.contact_id}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Actions Executed */}
            <Card className="bg-slate-50 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-900 flex items-center justify-between">
                  Actions Executed
                  <Badge variant="outline" className="border-slate-300">{result.actions_executed?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {result.actions_executed?.length > 0 ? (
                  <div className="space-y-2">
                    {result.actions_executed.map((action, idx) => {
                      const ActionIcon = actionIcons[action.action] || Zap;
                      return (
                        <div 
                          key={idx} 
                          className="flex items-center gap-3 p-2 rounded bg-white border border-slate-200"
                        >
                          <div className="flex items-center justify-center w-6 h-6 rounded bg-slate-100 text-slate-700 text-xs">
                            {idx + 1}
                          </div>
                          <ActionIcon className="h-4 w-4 text-slate-500" />
                          <span className="text-slate-900 capitalize flex-1">
                            {action.action?.replace(/_/g, ' ')}
                          </span>
                          {action.success ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-slate-500 text-center py-4">
                    {result.conditions_met === false ? 
                      'No actions executed - conditions were not met' : 
                      'No actions configured'}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Errors */}
            {result.errors?.length > 0 && (
              <Card className="bg-red-50 border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-red-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Errors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {result.errors.map((error, idx) => (
                      <div key={idx} className="text-sm text-red-600">
                        {error}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Dry Run Notice */}
            {result.dry_run && (
              <div className="flex items-center gap-2 p-3 rounded bg-blue-50 border border-blue-200">
                <AlertTriangle className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-700">
                  This was a test execution (dry run) - no actual actions were performed
                </span>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const WorkflowHistory = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { workflowId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(workflowId || 'all');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Fetch execution history
  const fetchHistory = useCallback(async () => {
    try {
      setRefreshing(true);
      let allHistory = [];
      
      if (selectedWorkflow && selectedWorkflow !== 'all') {
        // Fetch history for specific workflow
        const res = await fetch(
          `${API_URL}/api/workflow-builder/workflows/${selectedWorkflow}/history?token=${token}&limit=100`
        );
        const data = await res.json();
        if (data.success) {
          allHistory = data.history || [];
        }
      } else {
        // Fetch history for all workflows the user has access to
        const workflowsToFetch = workflows.slice(0, 10); // Limit to prevent too many requests
        const historyPromises = workflowsToFetch.map(w => 
          fetch(`${API_URL}/api/workflow-builder/workflows/${w.id}/history?token=${token}&limit=20`)
            .then(res => res.json())
            .then(data => data.success ? (data.history || []) : [])
            .catch(() => [])
        );
        
        const results = await Promise.all(historyPromises);
        allHistory = results.flat();
      }
      
      // Sort by execution date
      allHistory.sort((a, b) => new Date(b.executed_at) - new Date(a.executed_at));
      
      // Apply filters
      let filtered = allHistory;
      
      if (selectedOrg !== 'all') {
        filtered = filtered.filter(h => h.organization_id === selectedOrg);
      }
      
      if (statusFilter === 'success') {
        filtered = filtered.filter(h => h.result?.success && h.result?.conditions_met);
      } else if (statusFilter === 'failed') {
        filtered = filtered.filter(h => !h.result?.success || h.result?.errors?.length > 0);
      } else if (statusFilter === 'skipped') {
        filtered = filtered.filter(h => h.result?.conditions_met === false);
      }
      
      setHistory(filtered);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load execution history');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [token, selectedWorkflow, selectedOrg, statusFilter, workflows]);
  
  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [workflowsRes, orgsRes] = await Promise.all([
          fetch(`${API_URL}/api/workflow-builder/workflows?token=${token}`),
          fetch(`${API_URL}/api/workflow-builder/organizations?token=${token}`)
        ]);
        
        const workflowsData = await workflowsRes.json();
        const orgsData = await orgsRes.json();
        
        if (workflowsData.success) {
          setWorkflows(workflowsData.workflows || []);
        }
        if (orgsData.success) {
          setOrganizations(orgsData.organizations || []);
        }
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };
    
    fetchInitialData();
  }, [token]);
  
  // Fetch history when filters change
  useEffect(() => {
    if (workflows.length > 0 || selectedWorkflow !== 'all') {
      fetchHistory();
    }
  }, [workflows, selectedWorkflow, selectedOrg, statusFilter, fetchHistory]);
  
  const getStatusBadge = (execution) => {
    const result = execution.result || {};
    
    // Handle both new and legacy result formats
    if (result.success === true && result.conditions_met !== false) {
      return <Badge className="bg-green-100 text-green-700">Success</Badge>;
    } else if (result.conditions_met === false) {
      return <Badge className="bg-amber-100 text-amber-700">Skipped</Badge>;
    } else if (result.errors?.length > 0 || result.success === false) {
      return <Badge className="bg-red-100 text-red-700">Error</Badge>;
    } else if (result.status === 'completed' || result.status === 'executed') {
      return <Badge className="bg-green-100 text-green-700">Success</Badge>;
    } else {
      return <Badge className="bg-slate-100 text-slate-600">Executed</Badge>;
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/workflows-builder')}
                className="border-slate-200 text-slate-700 hover:bg-slate-100"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Execution History</h1>
                <p className="text-sm text-slate-500">View all workflow execution logs</p>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchHistory}
              disabled={refreshing}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-slate-500">
              <Filter className="h-4 w-4" />
              <span className="text-sm">Filters:</span>
            </div>
            
            <Select value={selectedWorkflow} onValueChange={setSelectedWorkflow}>
              <SelectTrigger className="w-[200px] bg-white border-slate-200" data-testid="workflow-filter">
                <SelectValue placeholder="All Workflows" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all">All Workflows</SelectItem>
                {workflows.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {isSuperAdmin && (
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger className="w-[200px] bg-white border-slate-200" data-testid="org-filter">
                  <SelectValue placeholder="All Organizations" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-white border-slate-200" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="ml-auto text-sm text-slate-500">
              {history.length} execution(s)
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : history.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Clock className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Execution History</h3>
              <p className="text-slate-500 text-center max-w-md">
                Workflow executions will appear here when triggers are activated.
                Test your workflows to generate execution logs.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Workflow</TableHead>
                  <TableHead className="text-slate-600">Trigger</TableHead>
                  <TableHead className="text-slate-600">Contact</TableHead>
                  <TableHead className="text-slate-600">Actions</TableHead>
                  <TableHead className="text-slate-600">Executed At</TableHead>
                  <TableHead className="text-slate-600 w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((execution) => {
                  const TriggerIcon = triggerIcons[execution.trigger] || Zap;
                  return (
                    <TableRow 
                      key={execution.id} 
                      className="border-slate-200 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSelectedExecution(execution)}
                      data-testid={`execution-row-${execution.id}`}
                    >
                      <TableCell>{getStatusBadge(execution)}</TableCell>
                      <TableCell className="text-slate-900 font-medium">
                        {execution.workflow_name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TriggerIcon className="h-4 w-4 text-slate-500" />
                          <span className="text-slate-700 capitalize">
                            {execution.trigger?.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {execution.context_summary?.contact_name || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-300">
                          {execution.result?.actions_executed?.length || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(execution.executed_at).toLocaleString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
      
      {/* Execution Detail Dialog */}
      <ExecutionDetailDialog
        execution={selectedExecution}
        open={!!selectedExecution}
        onOpenChange={(open) => !open && setSelectedExecution(null)}
      />
    </div>
  );
};

export default WorkflowHistory;
